import { useCallback, useMemo } from 'react';
import type { PhysicalCountFilterState } from '../../../types/physicalCount.types';
import type { ListPageActiveFilterChip } from '../../common/list-page';

export interface UsePhysicalCountFiltersProps {
  filters: PhysicalCountFilterState;
  setFilters: React.Dispatch<React.SetStateAction<PhysicalCountFilterState>>;
  appliedFilters: PhysicalCountFilterState;
  onClearAppliedField: (field: keyof PhysicalCountFilterState) => void;
  onSearch: () => void;
  onReset: () => void;
  locations?: Array<{ id: number; name: string }>;
}

export const usePhysicalCountFilters = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  onSearch,
  onReset,
  locations = [],
}: UsePhysicalCountFiltersProps) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [setFilters],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        onSearch();
      }
    },
    [onSearch],
  );

  const handleReset = useCallback(() => {
    setFilters({
      code: '',
      locationId: '',
    });
    onReset();
  }, [setFilters, onReset]);

  const handleSearch = useCallback(() => {
    onSearch();
  }, [onSearch]);

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = appliedFilters;

    if (applied.code) {
      chips.push({
        key: 'code',
        label: `Código: ${applied.code}`,
        onRemove: () => onClearAppliedField('code'),
      });
    }
    if (applied.locationId) {
      const locName =
        locations.find((l) => l.id.toString() === applied.locationId)?.name || applied.locationId;
      chips.push({
        key: 'locationId',
        label: `Sucursal: ${locName}`,
        onRemove: () => onClearAppliedField('locationId'),
      });
    }

    return chips;
  }, [appliedFilters, locations, onClearAppliedField]);

  return {
    handleChange,
    handleKeyDown,
    handleReset,
    handleSearch,
    activeFilterChips,
  };
};
